import './css/index.css';
import React, { createRef, useState, useEffect } from 'react';
import { func, number, exact, arrayOf, oneOfType, string, array } from 'prop-types';
import { max, min, extent } from 'd3-array';
import { select, selectAll, selection } from 'd3-selection';
import { timeDay } from 'd3-time';
import { area, line } from 'd3-shape';
import { brushX } from 'd3-brush';
import { getTextWidth } from '../../utils/svg/text-utils';

const RangeSlider = ( props ) => {

    const [ , setSliderData ] = useState( [] );
    const svgRef = createRef();

    const formatData = ( data ) => {

        const header = data[0];
        const dataValues = data.slice( 1 ).map( function ( values ) {

            return header.reduce( function ( o, k, i ) {

                o[k] = values[i];
                o.index = i - 1;

                return o;

            }, {} );

        } );

        return dataValues.map(
            ( { Date, ADS_Index } ) => {

                const dateParts = Date.split( ':' ).map( ( x ) => parseInt( x ) );

                const d = new window.Date( dateParts[0], dateParts[1] - 1, dateParts[2] );

                return { date: d, close: ADS_Index };

            } );

    };

    const margin = { top: 40,
        right: props.margin.right,
        bottom: 0,
        left: props.margin.left };
    const height = props.height;

    let previousSelection;

    selection.prototype.patternify = function ( params ) {

        const container = this;
        const selector = params.selector;
        const elementTag = params.tag;
        const data = params.data || [ selector ];

        // Pattern in action
        let selection = container.selectAll( `.${ selector }` ).data( data, ( d, i ) => {

            if ( typeof d === 'object' ) {

                if ( d.id ) {

                    return d.id;

                }

            }

            return i;

        } );


        selection.exit().remove();
        selection = selection.enter().append( elementTag )
            .merge( selection );
        selection.attr( 'class', selector );

        return selection;

    };

    useEffect( () => {

        const data = formatData( props.chartData );

        setSliderData( data );

        // set the ranges
        const x = props.makeXScale( props.chartWidth - props.margin.right - props.margin.left );

        // scale the range of the data
        x.domain( extent( data, function ( d ) {

            return d.date;

        } ) );

        const y = props.makeYScale( height );

        y.domain( [ min( data, function ( d ) {

            return d.close;

        } ), max( data, function ( d ) {

            return d.close;

        } ) ] );

        // appends a 'group' element to 'svg'
        // moves the 'group' element to the top left margin
        const svg = select( svgRef.current )
            .append( 'g' )
            .attr( 'transform',
                `translate(${ margin.left },${ margin.top })` );


        // define line helper function
        const linePath = line()
            .x( ( d ) => x( d.date ) )
            .y( ( d ) => y( d.close ) );

        // add line to chart
        svg.append( 'path' )
            .attr( 'd', linePath( data ) )
            .attr( 'stroke', '#000' )
            .attr( 'stroke-width', '1' )
            .attr( 'fill', 'none' );

        // define area helper function
        const areaPath = area()
            .x( ( d ) => x( d.date ) )
            .y1( ( d ) => y( d.close ) )
            .y0( y( min( data, function ( d ) {

                return d.close;

            } ) ) );

        // add the area
        svg.append( 'path' )
            .attr( 'stroke', '1px' )
            .attr( 'd', areaPath( data ) )
            .attr( 'class', 'slider-area' )
            .attr( 'fill', '#c6c6c6' )
            .attr( 'opacity', '0.3' );

        svg.append( 'text' )
            .attr( 'class', 'custom-handle-date-text' )
            .attr( 'x', ( props.chartWidth - margin.left - margin.right ) / 2 )
            .attr( 'y', -10 )
            .text( 'text text' );

        // initialize the brush area: start at 0,0 and finishes at width,height: it means I select the whole graph area
        const brush = brushX()
            .extent( [ [ 0, 0 ],
                [ props.chartWidth - margin.left - margin.right,
                    y(
                        min( data, function ( d ) {

                            return d.close;

                        } )
                    )
                ]
            ] )
            .on( 'brush', brushed )
            .on( 'end', brushEnded );

        const defaultSelection = [ x( x.domain()[0] ), x( x.domain()[1] ) ];

        // Create grouping for brush container
        const brushContainer = svg.patternify( { tag: 'g', selector: 'brush' } );

        // create groupings for handles and add data
        const handle = brushContainer
            .patternify( { tag: 'g',
                selector: 'custom-handle',
                data: [ { left: true }, { left: false } ] } )
            .attr( 'cursor', 'ew-resize' )
            .attr( 'pointer-events', 'all' );


        const handleBorderWidth = 0.25,
            handlerFill = '#3F434D',
            handleWidth = 12,
            handleHeight = 22,
            handleDotSize = 2,
            middleHandlerStroke = '#082655',
            middleHandlerFill = '#ffffff';

        // define left and right borders of selection
        handle
            .patternify( {
                tag: 'rect',
                selector: 'custom-handle-border',
                data: ( d ) => [ d ]
            } )
            .attr( 'width', handleBorderWidth )
            .attr( 'height', props.height + margin.top + margin.bottom )
            .attr( 'fill', handlerFill )
            .attr( 'stroke', handlerFill )
            .attr( 'y', 0 )
            .attr( 'x', 2 )
            .attr( 'pointer-events', 'none' );

        // create container for handles
        handle
            .patternify( {
                tag: 'rect',
                selector: 'custom-handle-rect-container',
                data: ( d ) => [ d ]
            } )
            .attr( 'width', handleWidth )
            .attr( 'height', handleHeight )
            .attr( 'fill', middleHandlerFill )
            .attr( 'stroke', middleHandlerStroke )
            .attr( 'y', handleHeight / 2 + 1 )
            .attr( 'x', -handleWidth / 2.5 )
            .attr( 'pointer-events', 'none' );

        handle
            .patternify( {
                tag: 'rect',
                selector: 'custom-handle-rect-line-top',
                data: ( d ) => [ d ]
            } )
            .attr( 'width', handleDotSize )
            .attr( 'height', handleDotSize )
            .attr( 'fill', middleHandlerStroke )
            .attr( 'stroke', middleHandlerStroke )
            .attr( 'y', 16 )
            .attr( 'x', 0 )
            .attr( 'pointer-events', 'none' );

        handle
            .patternify( {
                tag: 'rect',
                selector: 'custom-handle-rect-line-middle',
                data: ( d ) => [ d ]
            } )
            .attr( 'width', handleDotSize )
            .attr( 'height', handleDotSize )
            .attr( 'fill', middleHandlerStroke )
            .attr( 'stroke', middleHandlerStroke )
            .attr( 'y', 22 )
            .attr( 'x', 0 )
            .attr( 'pointer-events', 'none' );

        handle
            .patternify( {
                tag: 'rect',
                selector: 'custom-handle-rect-line-bottom',
                data: ( d ) => [ d ]
            } )
            .attr( 'width', handleDotSize )
            .attr( 'height', handleDotSize )
            .attr( 'fill', middleHandlerStroke )
            .attr( 'stroke', middleHandlerStroke )
            .attr( 'y', 28 )
            .attr( 'x', 0 )
            .attr( 'pointer-events', 'none' );

        // activate brush on page and select full selection
        brushContainer
            .call( brush )
            .call( brush.move, defaultSelection )
            .on( ' mousedown touchstart', brushOverlay );

        function brushed( { selection } ) {

            if ( !selection ) {

                brushContainer.call( brush.move, defaultSelection );

                return;

            }

            if ( selection ) {

                if ( selection[1] - selection[0] <= 15 ) {

                    selection = previousSelection;
                    brushContainer.call( brush.move, previousSelection );

                    return;

                }

                const value = selection.map( x.invert, x ).map( timeDay.floor, x );

                svg.property( 'value', value );

                svg.dispatch( 'input' );

                // Gets date value of indexes
                // const selectedDateRanges = selection.map( x.invert, x );

                props.updateRangeSelection( value );

                previousSelection = selection;

                handle
                    .attr( 'display', null )
                    .attr( 'transform', function ( d, i ) {

                        return `translate(${ selection[i] - 2 },${ ( props.height / 2 - 25 ) })`;

                    } );

                selectAll( '.custom-handle-date-text' )
                    .attr( 'x', ( d, i ) => {

                        const text = `${ value[0].getMonth() + 1 }/${ value[0].getDate() }/${ value[0].getFullYear() } – ${ value[1].getMonth() + 1 }/${ value[1].getDate() }/${ value[1].getFullYear() }`;
                        const n = getTextWidth( text, 12, 'Graphik-Bold' );

                        return ( ( props.chartWidth - margin.left - margin.right ) / 2 ) - ( n / 2 );

                    } )
                    .text( ( d, i ) => {

                        if ( i === 0 ) {

                            return `${ value[0].getMonth() + 1 }/${ value[0].getDate() }/${ value[0].getFullYear() } – ${ value[1].getMonth() + 1 }/${ value[1].getDate() }/${ value[1].getFullYear() }`;

                        }

                        return '';

                    } );

                // change selection color and opacity
                select( '.selection' )
                    .attr( 'fill', '#011e4d' )
                    .attr( 'fill-opacity', '0.08' );

                selectAll( '.custom-handle' ).raise();

            }

        }

        function brushEnded( { selection } ) {

            if ( !selection ) {

                brushContainer.call( brush.move, defaultSelection );

            }

        }

        function brushOverlay() {

            brushContainer.call( brush.move, previousSelection );

        }

    }, [] );

    return (
        <svg ref={ svgRef } width={ props.chartWidth } height={ props.height + margin.top + margin.bottom }>
        </svg>
    );

};

RangeSlider.propTypes = {
    updateRangeSelection: func.isRequired,
    margin: exact( { top: number, right: number, bottom: number, left: number } ).isRequired,
    chartData: arrayOf( oneOfType( [ string, number, array ] ) ).isRequired,
    chartWidth: number.isRequired,
    height: number.isRequired,
    makeXScale: func.isRequired,
    makeYScale: func.isRequired
};

export default RangeSlider;